/**
 * @fileoverview Molecule: the phrase itself, set as large as it will go.
 * @module components/molecules/PhraseBoard
 */

import { Skeleton } from "@/components/atoms"
import { toShout } from "@/lib/domain"
import type { AppMode, Phrase } from "@/types/types"

import "./PhraseBoard.css"

/** Props accepted by {@link PhraseBoard}. */
export interface PhraseBoardProps {
	/** The phrase to display, or null while none is resolved. */
	readonly phrase: Phrase | null
	/** The active mode, which decides the face it is set in. */
	readonly mode: AppMode
	/** True until the first phrase settles. */
	readonly isLoading: boolean
	/** True while the voice is mid-phrase. */
	readonly isSpeaking: boolean
}

/**
 * The phrase on display.
 *
 * @description
 * The region is a polite live region: a phrase drawn by clicking the
 * illustration has to reach a screen reader too, and the voice that shouts it
 * is not an accessibility feature — it can be missing, muted, or unsupported.
 *
 * @param props - Phrase, mode, and the two transient flags.
 * @returns The rendered board.
 */
export function PhraseBoard({
	phrase,
	mode,
	isLoading,
	isSpeaking,
}: PhraseBoardProps) {
	const classNames = [
		"phrase",
		`phrase--${mode}`,
		isSpeaking ? "phrase--speaking" : null,
	]
		.filter((value): value is string => value !== null)
		.join(" ")

	return (
		<div className={classNames} aria-live="polite" aria-busy={isLoading}>
			{isLoading || phrase === null ? (
				<div className="phrase__placeholder">
					<Skeleton width="80%" height="1.1em" />
					<Skeleton width="55%" height="1.1em" />
				</div>
			) : (
				<p className="phrase__text">{toShout(phrase.text)}</p>
			)}
		</div>
	)
}
